/**
 * Retry Manager for handling failed requests with exponential backoff
 */
import { createEnhancedError, ERROR_TYPES } from '../utils/errorHandling.js';
import { loadingManager, LOADING_TYPES } from '../stores/loadingStore.js';
import { appConfig } from '../config/appConfig.js';

// Default retry configuration
const DEFAULT_RETRY_CONFIG = {
  maxAttempts: appConfig.api.retryAttempts,
  baseDelay: 1000,        // Base delay in milliseconds
  maxDelay: appConfig.api.retryMaxDelay,  // Maximum delay in milliseconds (from config)
  backoffFactor: 2,       // Exponential backoff multiplier
  jitter: true           // Add random jitter to prevent thundering herd
};

// Retry-able error types
const RETRYABLE_ERROR_TYPES = new Set([
  ERROR_TYPES.NETWORK,
  ERROR_TYPES.TIMEOUT,
  ERROR_TYPES.SERVER
]);

/**
 * Retry Manager class for handling automatic retries with exponential backoff
 */
export class RetryManager {
  constructor(config = {}) {
    this.config = { ...DEFAULT_RETRY_CONFIG, ...config };
    this.activeRetries = new Map();
  }

  /**
   * Calculate delay for retry attempt with exponential backoff and jitter
   */
  calculateDelay(attempt, baseDelay = this.config.baseDelay) {
    const { backoffFactor, maxDelay, jitter } = this.config;

    // Calculate exponential delay
    let delay = baseDelay * Math.pow(backoffFactor, attempt - 1);

    // Cap at maximum delay
    delay = Math.min(delay, maxDelay);

    // Add jitter to prevent thundering herd problem
    if (jitter) {
      const jitterAmount = delay * 0.1; // 10% jitter
      delay += (Math.random() - 0.5) * 2 * jitterAmount;
    }

    return Math.max(0, Math.floor(delay));
  }

  /**
   * Check if an error is retryable
   */
  isRetryableError(error) {
    // Check if error has explicit retryable flag
    if (error.retryable !== undefined) {
      return error.retryable;
    }

    // Check error type
    if (error.type && RETRYABLE_ERROR_TYPES.has(error.type)) {
      return true;
    }

    // Check HTTP status codes
    if (error.response?.status) {
      const status = error.response.status;
      // Retry on 5xx server errors and specific 4xx errors
      return status >= 500 || status === 408 || status === 429;
    }

    // Check for network/timeout errors
    if (error.code === 'ECONNABORTED' ||
        error.code === 'NETWORK_ERROR' ||
        !error.response) {
      return true;
    }

    return false;
  }

  /**
   * Execute an operation with retry logic
   */
  async withRetry(operation, options = {}) {
    const {
      maxAttempts = this.config.maxAttempts,
      baseDelay = this.config.baseDelay,
      onRetry,
      onFailure,
      operationName = 'operation',
      context = {}
    } = options;

    const retryId = `${operationName}-${Date.now()}`;
    let lastError = null;

    // Track this retry operation
    this.activeRetries.set(retryId, {
      operationName,
      startTime: new Date(),
      attempts: 0,
      maxAttempts,
      context
    });

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        // Update retry tracking
        const retryInfo = this.activeRetries.get(retryId);
        retryInfo.attempts = attempt;
        retryInfo.lastAttemptTime = new Date();

        // Execute the operation
        const result = await operation(attempt, retryId);

        // Success - clean up and return result
        this.activeRetries.delete(retryId);
        return result;

      } catch (error) {
        lastError = error;

        // Log the attempt
        console.warn(`Attempt ${attempt}/${maxAttempts} failed for ${operationName}:`, error);

        // If this is the last attempt or error is not retryable, throw
        if (attempt >= maxAttempts || !this.isRetryableError(error)) {
          this.activeRetries.delete(retryId);

          // Call failure callback if provided
          if (onFailure) {
            onFailure(error, attempt, retryId);
          }

          throw error;
        }

        // Calculate delay before next retry
        const delay = this.calculateDelay(attempt, baseDelay);

        // Call retry callback if provided
        if (onRetry) {
          onRetry(error, attempt, delay, retryId);
        }

        // Wait before retrying
        if (delay > 0) {
          await this.delay(delay);
        }
      }
    }

    // This should not be reached, but just in case
    this.activeRetries.delete(retryId);
    throw lastError;
  }

  /**
   * Retry with loading state management
   */
  async withRetryAndLoading(operation, loadingType, options = {}) {
    const {
      loadingMessage = 'Processing request...',
      retryMessage = 'Retrying request...',
      ...retryOptions
    } = options;

    let loadingOperationId = null;

    return this.withRetry(operation, {
      ...retryOptions,
      onRetry: (error, attempt, delay, retryId) => {
        // Stop current loading operation
        if (loadingOperationId) {
          loadingManager.stop(loadingOperationId);
        }

        // Start new loading operation for retry
        loadingOperationId = loadingManager.start(LOADING_TYPES.RETRY, {
          statusMessage: `${retryMessage} (Attempt ${attempt + 1})`,
          cancellable: false
        });

        // Call original onRetry if provided
        if (retryOptions.onRetry) {
          retryOptions.onRetry(error, attempt, delay, retryId);
        }
      },
      onFailure: (error, attempt, retryId) => {
        // Stop loading operation on final failure
        if (loadingOperationId) {
          loadingManager.stop(loadingOperationId);
        }

        // Call original onFailure if provided
        if (retryOptions.onFailure) {
          retryOptions.onFailure(error, attempt, retryId);
        }
      }
    });
  }

  /**
   * Cancel a retry operation
   */
  cancelRetry(retryId) {
    if (this.activeRetries.has(retryId)) {
      this.activeRetries.delete(retryId);
      return true;
    }
    return false;
  }

  /**
   * Get information about active retries
   */
  getActiveRetries() {
    return Array.from(this.activeRetries.entries()).map(([id, info]) => ({
      id,
      ...info,
      duration: Date.now() - info.startTime.getTime()
    }));
  }

  /**
   * Cancel all active retries
   */
  cancelAllRetries() {
    const cancelled = this.activeRetries.size;
    this.activeRetries.clear();
    return cancelled;
  }

  /**
   * Utility method for delay
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

/**
 * Enhanced retry manager with operation-specific configurations
 */
export class APIRetryManager extends RetryManager {
  constructor() {
    super();

    // Operation-specific retry configurations
    this.operationConfigs = {
      viewport: {
        maxAttempts: 3,
        baseDelay: 1000,
        operationName: 'viewport-load'
      },
      filter: {
        maxAttempts: 2,
        baseDelay: 800,
        operationName: 'filter-apply'
      },
      search: {
        maxAttempts: 2,
        baseDelay: 500,
        operationName: 'mine-search'
      }
    };
  }

  /**
   * Retry viewport loading operation
   */
  async retryViewportLoad(operation, context = {}) {
    const config = this.operationConfigs.viewport;

    return this.withRetryAndLoading(
      operation,
      LOADING_TYPES.VIEWPORT,
      {
        ...config,
        loadingMessage: 'Loading mines in area...',
        retryMessage: 'Retrying mine data load...',
        context: { operation: 'viewport', ...context },
        onRetry: (error, attempt, delay) => {
          console.log(`Retrying viewport load (attempt ${attempt + 1}) after ${delay}ms`);
        }
      }
    );
  }

  /**
   * Retry filter application
   */
  async retryFilterApplication(operation, context = {}) {
    const config = this.operationConfigs.filter;

    return this.withRetryAndLoading(
      operation,
      LOADING_TYPES.FILTER,
      {
        ...config,
        loadingMessage: 'Applying filters...',
        retryMessage: 'Retrying filter application...',
        context: { operation: 'filter', ...context },
        onRetry: (error, attempt, delay) => {
          console.log(`Retrying filter application (attempt ${attempt + 1}) after ${delay}ms`);
        }
      }
    );
  }

  /**
   * Retry search operation
   */
  async retrySearch(operation, context = {}) {
    const config = this.operationConfigs.search;

    return this.withRetryAndLoading(
      operation,
      LOADING_TYPES.SEARCH,
      {
        ...config,
        loadingMessage: 'Searching mines...',
        retryMessage: 'Retrying search...',
        context: { operation: 'search', ...context },
        onRetry: (error, attempt, delay) => {
          console.log(`Retrying search (attempt ${attempt + 1}) after ${delay}ms`);
        }
      }
    );
  }
}

// Export singleton instance for API operations
export const apiRetryManager = new APIRetryManager();

// RetryManager class is already exported above via class declaration